import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Row, Col, Alert, Table } from 'react-bootstrap';
import { BsPencilSquare, BsPlusCircle, BsSave, BsXCircle } from 'react-icons/bs';
import api from '../lib/api';

const emptyBrand = {
  name: '',
  slug: '',
  allowed_domains: '',
  fitment_enabled: false,
  fitment_field_order: '',
  intent_patterns: [],
  link_templates: [
    { link_type: 'keyword', template: '', active: true },
    { link_type: 'fitment', template: '', active: true },
    { link_type: 'alternate', template: '', active: true },
  ],
  active: true,
};

const LINK_LABEL = {
  keyword: 'Keyword Search',
  fitment: 'Fitment Search',
  alternate: 'Alternate Search',
};

const BrandRouterCard = () => {
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/brand-router/');
      setBrands(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMsg('Failed to load brand router config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing({ ...emptyBrand });
  };

  const editBrand = (b) => {
    const ltMap = {};
    (b.link_templates || []).forEach((lt) => {
      ltMap[lt.link_type] = lt;
    });

    const fullLT = ['keyword', 'fitment', 'alternate'].map((t) =>
      ltMap[t] ? { ...ltMap[t] } : { link_type: t, template: '', active: true }
    );

    setEditing({ ...b, link_templates: fullLT });
  };

  const upsert = async () => {
    setMsg('');
    try {
      const res = await api.post('/brand-router/', editing);
      setMsg(res?.data?.message || 'Brand router saved successfully.');
      await load();
      setEditing(null);
      setTimeout(() => setMsg(''), 1800);
    } catch (e) {
      const detail = e?.response?.data || e?.message || 'Save failed';
      setMsg(`Error: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    }
  };

  const updateLinkTemplate = (link_type, key, value) => {
    setEditing((prev) => {
      const next = { ...prev };
      const idx = next.link_templates.findIndex((l) => l.link_type === link_type);
      if (idx === -1) next.link_templates.push({ link_type, template: '', active: true });
      const i = idx === -1 ? next.link_templates.length - 1 : idx;
      next.link_templates[i][key] = value;
      return next;
    });
  };

  const addIntent = () => {
    setEditing((prev) => ({
      ...prev,
      intent_patterns: [...(prev.intent_patterns || []), { pattern: '', priority: 100 }],
    }));
  };

  const removeIntent = (i) => {
    setEditing((prev) => {
      const arr = [...prev.intent_patterns];
      arr.splice(i, 1);
      return { ...prev, intent_patterns: arr };
    });
  };

  if (loading) {
    return (
      <Card className="admin-surface-card">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="admin-surface-card">
      <Card.Body>
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="mb-0 fw-bold">Dynamic Brand Router</h5>
        </div>

        {msg ? (
          <Alert variant={msg.startsWith('Error') ? 'danger' : 'success'}>
            {msg}
          </Alert>
        ) : null}

        {!editing && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="admin-section-title mb-0">Active Brands</div>
              <Button
                size="sm"
                onClick={startNew}
                className="admin-action-btn d-inline-flex align-items-center gap-2"
              >
                <BsPlusCircle />
                New Brand
              </Button>
            </div>

            <div className="table-responsive">
              <Table hover className="admin-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Slug</th>
                    <th>Name</th>
                    <th>Allowed Domains</th>
                    <th>Fitment</th>
                    <th>Field Order</th>
                    <th>Intents</th>
                    <th>Links</th>
                    <th>Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {brands.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        No brands configured
                      </td>
                    </tr>
                  ) : (
                    brands.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <code>{b.slug}</code>
                        </td>
                        <td>{b.name}</td>
                        <td style={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>
                          {b.allowed_domains}
                        </td>
                        <td>{b.fitment_enabled ? 'Yes' : 'No'}</td>
                        <td>{b.fitment_field_order}</td>
                        <td style={{ maxWidth: 260 }}>
                          {(b.intent_patterns || []).map((p) => (
                            <div key={p.id || `${p.pattern}-${p.priority}`}>
                              <code>{p.pattern}</code>{' '}
                              <small className="text-muted">({p.priority})</small>
                            </div>
                          ))}
                        </td>
                        <td style={{ maxWidth: 260 }}>
                          {(b.link_templates || []).map((lt) => (
                            <div key={lt.id || lt.link_type}>
                              <strong>{LINK_LABEL[lt.link_type] || lt.link_type}</strong>:{' '}
                              <code>{lt.template}</code>
                            </div>
                          ))}
                        </td>
                        <td>
                          {b.updated_at ? new Date(b.updated_at).toLocaleString() : '-'}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="admin-action-btn d-inline-flex align-items-center gap-1"
                            onClick={() => editBrand(b)}
                          >
                            <BsPencilSquare />
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {editing && (
          <>
            <div className="admin-section-title mt-2">Edit Brand</div>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Slug</Form.Label>
                  <Form.Control
                    value={editing.slug}
                    onChange={(e) =>
                      setEditing({ ...editing, slug: e.target.value.toLowerCase() })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-3">
              <Form.Label>Allowed Domains</Form.Label>
              <Form.Control
                value={editing.allowed_domains}
                onChange={(e) =>
                  setEditing({ ...editing, allowed_domains: e.target.value })
                }
                placeholder="example.com,app.example.com"
              />
            </Form.Group>

            <Row className="g-3 mt-1">
              <Col md={4}>
                <Form.Check
                  type="switch"
                  label="Fitment Enabled"
                  checked={!!editing.fitment_enabled}
                  onChange={(e) =>
                    setEditing({ ...editing, fitment_enabled: e.target.checked })
                  }
                />
              </Col>

              <Col md={8}>
                <Form.Group>
                  <Form.Label>Fitment Field Order</Form.Label>
                  <Form.Control
                    value={editing.fitment_field_order}
                    onChange={(e) =>
                      setEditing({ ...editing, fitment_field_order: e.target.value })
                    }
                    placeholder="make,model,year,vehicleType,engineType"
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <h6 className="fw-bold">Intent Patterns</h6>
            {(editing.intent_patterns || []).map((p, i) => (
              <Row className="g-2 align-items-end mb-2" key={i}>
                <Col md={9}>
                  <Form.Group>
                    <Form.Label className="mb-1">Pattern</Form.Label>
                    <Form.Control
                      value={p.pattern}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditing((prev) => {
                          const arr = [...prev.intent_patterns];
                          arr[i] = { ...arr[i], pattern: val };
                          return { ...prev, intent_patterns: arr };
                        });
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group>
                    <Form.Label className="mb-1">Priority</Form.Label>
                    <Form.Control
                      type="number"
                      value={p.priority}
                      onChange={(e) => {
                        const val = parseInt(e.target.value || '0', 10);
                        setEditing((prev) => {
                          const arr = [...prev.intent_patterns];
                          arr[i] = { ...arr[i], priority: val };
                          return { ...prev, intent_patterns: arr };
                        });
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={1}>
                  <Button
                    variant="outline-danger"
                    className="admin-action-btn"
                    onClick={() => removeIntent(i)}
                  >
                    <BsXCircle />
                  </Button>
                </Col>
              </Row>
            ))}

            <Button
              size="sm"
              variant="secondary"
              className="mb-3 admin-action-btn d-inline-flex align-items-center gap-2"
              onClick={addIntent}
            >
              <BsPlusCircle />
              Add Intent
            </Button>

            <hr />

            <h6 className="fw-bold">Link Templates</h6>

            {['keyword', 'fitment', 'alternate'].map((lt) => {
              const row =
                (editing.link_templates || []).find((x) => x.link_type === lt) || {
                  link_type: lt,
                  template: '',
                  active: true,
                };

              return (
                <Card className="admin-surface-card mb-3" key={lt}>
                  <Card.Body>
                    <Row className="g-2 align-items-end">
                      <Col md={3}>
                        <strong>{LINK_LABEL[lt]}</strong>
                      </Col>

                      <Col md={7}>
                        <Form.Control
                          placeholder={
                            lt === 'keyword'
                              ? 'https://domain.com/search?keyword={query}'
                              : lt === 'fitment'
                              ? 'https://domain.com/product?make={make}&model={model}&year={year}'
                              : 'https://domain.com/alt?search={query}'
                          }
                          value={row.template}
                          onChange={(e) =>
                            updateLinkTemplate(lt, 'template', e.target.value)
                          }
                        />
                      </Col>

                      <Col md={2}>
                        <Form.Check
                          type="switch"
                          label="Active"
                          checked={!!row.active}
                          onChange={(e) =>
                            updateLinkTemplate(lt, 'active', e.target.checked)
                          }
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}

            <Row className="g-2 mt-2">
              <Col md={3}>
                <Form.Check
                  type="switch"
                  label="Brand Active"
                  checked={!!editing.active}
                  onChange={(e) =>
                    setEditing({ ...editing, active: e.target.checked })
                  }
                />
              </Col>
            </Row>

            <div className="d-flex flex-wrap gap-2 mt-4">
              <Button
                onClick={upsert}
                className="admin-action-btn d-inline-flex align-items-center gap-2"
              >
                <BsSave />
                Save
              </Button>

              <Button
                variant="secondary"
                onClick={() => setEditing(null)}
                className="admin-action-btn"
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default BrandRouterCard;